import { AlertCircle, WifiOff, Clock, FileQuestion } from 'lucide-react';
import { Button } from '@nextui-org/react';

type ErrorType = '404' | '500' | 'network' | 'timeout';

interface VendorDetailErrorProps {
  type: ErrorType;
  vendorName?: string;
  onRetry?: () => void;
  onGoBack?: () => void;
  className?: string;
}

const ERROR_CONFIG: Record<
  ErrorType,
  {
    icon: React.ReactNode;
    heading: string;
    description: string;
    showRetry: boolean;
    showGoBack: boolean;
  }
> = {
  '404': {
    icon: <FileQuestion className="w-16 h-16 text-gray-400" />,
    heading: 'Vendor not found',
    description: "The vendor you're looking for doesn't exist or may have been removed.",
    showRetry: false,
    showGoBack: true,
  },
  '500': {
    icon: <AlertCircle className="w-16 h-16 text-red-400" />,
    heading: 'Something went wrong',
    description: 'Failed to load vendor details. Please try again.',
    showRetry: true,
    showGoBack: true,
  },
  network: {
    icon: <WifiOff className="w-16 h-16 text-gray-400" />,
    heading: 'Connection lost',
    description: 'Check your internet connection and try again.',
    showRetry: true,
    showGoBack: true,
  },
  timeout: {
    icon: <Clock className="w-16 h-16 text-yellow-500" />,
    heading: 'Request timed out',
    description: 'The server took too long to respond. Please try again.',
    showRetry: true,
    showGoBack: true,
  },
};

export const VendorDetailError = ({
  type,
  vendorName,
  onRetry,
  onGoBack,
  className = '',
}: VendorDetailErrorProps) => {
  const config = ERROR_CONFIG[type];

  const handleRetry = () => {
    onRetry?.();
  };

  const handleGoBack = () => {
    onGoBack?.();
  };

  // Build description with vendor name for 404
  const description =
    type === '404' && vendorName
      ? `The vendor "${vendorName}" doesn't exist or may have been removed.`
      : config.description;

  return (
    <div
      data-testid="vendor-detail-error"
      role="alert"
      aria-live="polite"
      className={`flex items-center justify-center min-h-[400px] p-8 ${className}`}
    >
      <div className="flex flex-col items-center text-center max-w-md">
        {/* Illustration/Icon */}
        <div data-testid="error-illustration" className="mb-6">
          {config.icon}
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {config.heading}
        </h2>

        {/* Description */}
        <p className="text-gray-600 mb-6">{description}</p>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {config.showRetry && (
            <Button
              color="primary"
              onClick={handleRetry}
              aria-label="Retry loading vendor details"
            >
              Retry
            </Button>
          )}
          {config.showGoBack && (
            <Button
              variant={config.showRetry ? 'bordered' : 'solid'}
              color={config.showRetry ? 'default' : 'primary'}
              onClick={handleGoBack}
              aria-label="Go back to vendor list"
            >
              Go Back
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
